// Tesseract removed - no longer used
import { normalizeName, looksLikePlayerLine } from './nameNormalize';

/**
 * Extract text from an image using Tesseract OCR
 * @param {File} imageFile - The image file to process
 * @param {Function} onProgress - Optional progress callback (progress: 0-1)
 * @returns {Promise<string>} - The extracted text
 */
/**
 * Pre-process image to improve OCR accuracy
 * @param {File} imageFile - The original image file
 * @returns {Promise<string>} - Data URL of processed image
 */
const preprocessImage = async (imageFile) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      // Set canvas size to image size
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Convert to grayscale and increase contrast
      for (let i = 0; i < data.length; i += 4) {
        // Grayscale conversion
        const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
        
        // Increase contrast - make light colors white, dark colors black
        const threshold = 128;
        const newValue = gray > threshold ? 255 : 0;
        
        data[i] = newValue;     // R
        data[i + 1] = newValue; // G
        data[i + 2] = newValue; // B
        // Alpha stays the same
      }
      
      // Put processed image back
      ctx.putImageData(imageData, 0, 0);
      
      // Convert to data URL
      resolve(canvas.toDataURL('image/png'));
    };
    
    img.onerror = reject;
    img.src = URL.createObjectURL(imageFile);
  });
};

export const extractTextFromImage = async (imageFile, onProgress = null) => {
  throw new Error('Tesseract OCR has been removed. Use Google Vision or GPT-4 Vision instead.');
};

/**
 * Parse NBA 2K box score text into structured data
 * @param {string} text - Raw OCR text
 * @param {Array} players - List of players from database for matching
 * @returns {Object} - Parsed game data
 */
export const parseBoxScore = (text, players = []) => {
  const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  
  const result = {
    teamName: null,
    players: [],
    errors: []
  };

  // Try to find team name (usually at the top)
  const teamLine = lines.find(line => 
    line.includes('Bucks') || line.includes('Pelicans') || line.includes('Hornets') ||
    line.includes('Cavaliers') || line.includes('Rockets') || line.includes('Grizzlies') ||
    // Add more team names as needed
    line.length > 10 && line.length < 50
  );
  
  if (teamLine) {
    result.teamName = teamLine;
  }

  // Find player stat lines
  // Looking for pattern: Name MIN PTS REB AST STL BLK TO FG 3PT FT OR FLS
  // Example: "L. Ball 26 15 8 7 4 0 3 6-10 1-1 2-4 0 3"
  
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Skip obviously non-player lines
    if (line.length < 15) continue; // Too short
    if (line.toUpperCase().includes('DNP')) continue; // DNP lines handled separately
    if (line.includes('PLAYER NAME') || line.includes('MIN') && line.includes('PTS') && line.includes('REB')) continue; // Header
    
    // More flexible player name detection:
    // - Single letter + dot + optional space + name: "J. Brunson" or "J.Brunson"
    // - First initial + last name: "F Wagner" or "F. Wagner"
    // - Full name: "Jalen Brunson"
    const hasNumbers = /\d/.test(line);
    const startsWithName = looksLikePlayerLine(line);
    
    // Check for stat-like patterns (fractions like "10-16" or "0-0")
    const hasStatPattern = /\d+[/-]\d+/.test(line) || (line.match(/\d+/g) || []).length >= 5;
    
    if (hasNumbers && startsWithName && hasStatPattern) {
      try {
        const playerStat = parsePlayerLine(line, players);
        if (playerStat) {
          result.players.push(playerStat);
        }
      } catch (error) {
        result.errors.push(`Failed to parse line: ${line} - ${error.message}`);
      }
    }
  }
  

  return result;
};

/**
 * Parse a single player stat line
 * @param {string} line - Single line of player stats
 * @param {Array} players - List of players from database
 * @returns {Object|null} - Parsed player stats
 */
const parsePlayerLine = (line, players) => {
  // Clean the line - remove extra spaces, normalize separators
  const cleanedLine = line
    .replace(/\s+/g, ' ')
    .replace(/[|]/g, ' ') // Remove table separators
    .replace(/\s*[-–—]\s*/g, '/') // Normalize fractions: "6 - 10" or "6 – 10" -> "6/10"
    .replace(/\s*\/\s*/g, '/') // Remove spaces around slashes
    .replace(/[O]/g, '0') // Fix common OCR mistake: O vs 0
    .trim();
  
  
  const parts = cleanedLine.split(' ');
  
  
  if (parts.length < 5) { // Even more lenient - just need name + a few stats
    return null;
  }

  // Extract player name (first 1-3 parts depending on format)
  let nameEndIndex = 1;
  let playerName = parts[0];
  
  // Check if second part is also part of name (not a number and not a slash)
  if (parts[1] && isNaN(parts[1]) && !parts[1].includes('/')) {
    playerName += ' ' + parts[1];
    nameEndIndex = 2;
    
    // Check for third part of name (rare but possible)
    if (parts[2] && isNaN(parts[2]) && !parts[2].includes('/')) {
      playerName += ' ' + parts[2];
      nameEndIndex = 3;
    }
  }

  // Rest are stats
  const stats = parts.slice(nameEndIndex);

  // Match player from database
  const matchedPlayer = matchPlayerByName(playerName, players);

  // Initialize player stat object
  const playerStat = {
    originalName: playerName,
    playerName: matchedPlayer ? `${matchedPlayer.firstName} ${matchedPlayer.lastName}` : playerName,
    playerId: matchedPlayer?.id || null,
    matchConfidence: matchedPlayer?.confidence || 'low',
    possibleMatches: matchedPlayer?.alternatives || [],
    stats: {
      minutes: 0,
      points: 0,
      rebounds: 0,
      assists: 0,
      steals: 0,
      blocks: 0,
      turnovers: 0,
      fieldGoalsMade: 0,
      fieldGoalsAttempted: 0,
      threePointersMade: 0,
      threePointersAttempted: 0,
      freeThrowsMade: 0,
      freeThrowsAttempted: 0,
      offensiveRebounds: 0,
      fouls: 0
    }
  };

  // Parse stats more flexibly - look for patterns rather than fixed positions
  let statIndex = 0;
  let foundFractions = 0;
  
  for (let i = 0; i < stats.length && statIndex < 13; i++) {
    const value = stats[i];
    
    // Check if it's a fraction (FG, 3PT, FT)
    const fractionMatch = value.match(/(\d+)[\/](\d+)/);
    if (fractionMatch) {
      foundFractions++;
      const made = parseInt(fractionMatch[1]) || 0;
      const attempted = parseInt(fractionMatch[2]) || 0;
      
      if (foundFractions === 1) {
        // First fraction is FG
        playerStat.stats.fieldGoalsMade = made;
        playerStat.stats.fieldGoalsAttempted = attempted;
      } else if (foundFractions === 2) {
        // Second fraction is 3PT
        playerStat.stats.threePointersMade = made;
        playerStat.stats.threePointersAttempted = attempted;
      } else if (foundFractions === 3) {
        // Third fraction is FT
        playerStat.stats.freeThrowsMade = made;
        playerStat.stats.freeThrowsAttempted = attempted;
      }
      continue;
    }
    
    // Regular number stat
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      // Order: MIN, PTS, REB, AST, STL, BLK, TO, [FG], [3PT], [FT], OR, FLS
      if (statIndex === 0) playerStat.stats.minutes = numValue;
      else if (statIndex === 1) playerStat.stats.points = numValue;
      else if (statIndex === 2) playerStat.stats.rebounds = numValue;
      else if (statIndex === 3) playerStat.stats.assists = numValue;
      else if (statIndex === 4) playerStat.stats.steals = numValue;
      else if (statIndex === 5) playerStat.stats.blocks = numValue;
      else if (statIndex === 6) playerStat.stats.turnovers = numValue;
      else if (statIndex === 7 && foundFractions === 0) {
        // If no fractions found yet, this might be FG made (old format)
        playerStat.stats.fieldGoalsMade = numValue;
      } else if (statIndex >= 7 && foundFractions === 3) {
        // After all fractions, next two are OR and FLS
        if (statIndex === 7) playerStat.stats.offensiveRebounds = numValue;
        else if (statIndex === 8) playerStat.stats.fouls = numValue;
      }
      statIndex++;
    }
  }

  // Validation: at least have minutes or points (be very lenient)
  if (playerStat.stats.minutes === 0 && playerStat.stats.points === 0) {
    return null;
  }

  return playerStat;
};

/**
 * Match OCR player name to database player
 * @param {string} ocrName - Name from OCR (e.g., "L. Ball")
 * @param {Array} players - List of players from database
 * @returns {Object|null} - Matched player with confidence
 */
const matchPlayerByName = (ocrName, players) => {
  if (!players || players.length === 0) return null;

  const cleanName = normalizeName(ocrName);
  const parts = cleanName.split(' ');

  // Check for initial + last name format (e.g., "L Ball" or "L. Ball")
  if (parts.length === 2 && parts[0].length === 1) {
    const initial = parts[0];
    const lastName = parts[1];

    const matches = players.filter(p =>
      normalizeName(p.firstName).startsWith(initial) &&
      normalizeName(p.lastName) === lastName
    );

    if (matches.length === 1) {
      return {
        ...matches[0],
        confidence: 'high'
      };
    } else if (matches.length > 1) {
      return {
        ...matches[0],
        confidence: 'medium',
        alternatives: matches.slice(1)
      };
    }
  }

  // Check for full name match
  if (parts.length >= 2) {
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];

    const match = players.find(p =>
      normalizeName(p.firstName) === firstName &&
      normalizeName(p.lastName) === lastName
    );

    if (match) {
      return {
        ...match,
        confidence: 'high'
      };
    }

    // Fuzzy match on last name only
    const lastNameMatches = players.filter(p =>
      normalizeName(p.lastName) === lastName
    );

    if (lastNameMatches.length > 0) {
      return {
        ...lastNameMatches[0],
        confidence: 'low',
        alternatives: lastNameMatches.slice(1)
      };
    }
  }

  return null;
};

/**
 * Validate extracted stats for reasonableness
 * @param {Object} playerStat - Parsed player stats
 * @returns {Array} - Array of warnings
 */
export const validateStats = (playerStat) => {
  const warnings = [];
  const { stats } = playerStat;

  // Check minutes
  if (stats.minutes < 0 || stats.minutes > 48) {
    warnings.push('Minutes should be between 0 and 48');
  }

  // Check made vs attempted
  if (stats.fieldGoalsMade > stats.fieldGoalsAttempted) {
    warnings.push('Field goals made cannot exceed attempted');
  }
  if (stats.threePointersMade > stats.threePointersAttempted) {
    warnings.push('Three pointers made cannot exceed attempted');
  }
  if (stats.freeThrowsMade > stats.freeThrowsAttempted) {
    warnings.push('Free throws made cannot exceed attempted');
  }

  // Check if points match scoring
  const calculatedPoints = 
    (stats.fieldGoalsMade - stats.threePointersMade) * 2 + 
    stats.threePointersMade * 3 + 
    stats.freeThrowsMade;

  if (Math.abs(calculatedPoints - stats.points) > 2) {
    warnings.push(`Points (${stats.points}) don't match calculated (${calculatedPoints})`);
  }

  // Check offensive rebounds don't exceed total
  if (stats.offensiveRebounds > stats.rebounds) {
    warnings.push('Offensive rebounds cannot exceed total rebounds');
  }

  return warnings;
};
